import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { strategyAPI } from '../../api/strategy';
import { formatPnL, getPnLColor } from '../../utils/formatters';

/* ══════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════ */
const C = {
  green: '#00e5a0', red: '#ff4d6d', blue: '#3d8ef0',
  amber: '#f5a623', purple: '#a78bfa',
  surface: '#0c1120', surface2: '#111827',
  border: '#1e2d45', border2: '#2a3f5f',
  muted: '#5a6a82', muted2: '#8899b0',
};
const mono = "'DM Mono', monospace";
const sans = "'Outfit', sans-serif";

/* ── Lot sizes per instrument ── */
const LOT_SIZE = {
  NIFTY:     65,
  BANKNIFTY: 30,
};

/* Get lot count from order */
const getLots = (order) => {
  const qty        = order.sell_leg?.quantity ?? order.buy_leg?.quantity ?? order.lot_size ?? 0;
  const instrument = (order.instrument ?? '').toUpperCase();
  const lotSize    = LOT_SIZE[instrument];
  if (!lotSize || !qty) return 1;
  return Math.max(1, Math.round(qty / lotSize));
};

/* Normalize PnL to 1 lot */
const normalizePnl = (pnl, order) => {
  if (pnl == null) return null;
  const lots = getLots(order);
  return lots > 1 ? pnl / lots : pnl;
};

/* ══════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════ */
const pill = (label, color, bg) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 9px', borderRadius: 100,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
    fontFamily: mono, color, background: bg,
    border: `1px solid ${color}33`,
  }}>{label}</span>
);

/* ══════════════════════════════════════════════
   TOOLTIP
══════════════════════════════════════════════ */
function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', fontFamily: mono, fontSize: 12, minWidth: 160 }}>
      <div style={{ color: C.muted2, marginBottom: 6, fontSize: 11 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: p.color ?? '#fff', marginBottom: 2 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>₹{Number(p.value).toLocaleString('en-IN')}</span>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   STAT CARD
══════════════════════════════════════════════ */
function StatCard({ label, value, sub, accent, valColor }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 16, padding: '20px',
      position: 'relative', overflow: 'hidden', flex: 1, minWidth: 0,
      transition: 'border-color 0.2s, transform 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.border2; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;  e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      <div style={{ fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 10, fontFamily: mono }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: mono, color: valColor }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted2, marginTop: 6, fontFamily: mono }}>{sub}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   CHART BLOCK
══════════════════════════════════════════════ */
function Block({ title, children, badge }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 18 }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: sans }}>{title}</span>
        {badge}
      </div>
      <div style={{ padding: '20px 18px' }}>{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   SIMPLE DISCLAIMER  — 1 line, red/amber
══════════════════════════════════════════════ */
function Disclaimer() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 14px',
      background: 'rgba(255,77,109,0.06)',
      border: '1px solid rgba(255,77,109,0.2)',
      borderRadius: 8, marginBottom: 16,
      fontSize: 11, color: '#ff8fa3',
      fontFamily: "'DM Mono', monospace",
    }}>
      <span style={{ fontSize: 13 }}>⚠</span>
      All PnL figures shown per 1 lot — NIFTY (65 qty) · BANKNIFTY (30 qty)
    </div>
  );
}

/* ══════════════════════════════════════════════
   FILTER BAR  — no status filter (always closed)
══════════════════════════════════════════════ */
function FilterBar({ filters, onChange, onReset }) {
  const inputStyle = {
    background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
    color: '#e8edf5', fontFamily: mono, fontSize: 12,
    padding: '7px 10px', outline: 'none', minWidth: 120, cursor: 'pointer',
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '1px', color: C.muted, fontFamily: mono,
    display: 'block', marginBottom: 5,
  };
  const hasActive = Object.values(filters).some(v => v !== '');

  return (
    <div className="st-filter-bar" style={{
      display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap',
      padding: '14px 18px', background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 16, marginBottom: 20,
    }}>
      <div className="st-filter-item" style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={labelStyle}>Signal</label>
        <select style={inputStyle} value={filters.signal} onChange={e => onChange('signal', e.target.value)}>
          <option value="">All signals</option>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
      </div>

      <div className="st-filter-item" style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={labelStyle}>Instrument</label>
        <select style={inputStyle} value={filters.instrument} onChange={e => onChange('instrument', e.target.value)}>
          <option value="">All</option>
          <option value="NIFTY">NIFTY</option>
          <option value="BANKNIFTY">BANKNIFTY</option>
        </select>
      </div>

      <div className="st-filter-item" style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={labelStyle}>From</label>
        <input type="date" style={inputStyle} value={filters.from_date} onChange={e => onChange('from_date', e.target.value)} />
      </div>

      <div className="st-filter-item" style={{ display: 'flex', flexDirection: 'column' }}>
        <label style={labelStyle}>To</label>
        <input type="date" style={inputStyle} value={filters.to_date} onChange={e => onChange('to_date', e.target.value)} />
      </div>

      {hasActive && (
        <button
          onClick={onReset}
          style={{ padding: '7px 14px', background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.25)', borderRadius: 8, color: C.red, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: mono, alignSelf: 'flex-end' }}
        >
          ✕ Clear
        </button>
      )}

      {/* Always-on closed filter note */}
      <div className="st-filter-note" style={{ marginLeft: 'auto', alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.muted2, fontFamily: mono }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.muted, display: 'inline-block' }} />
        Closed trades only
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ORDERS TABLE  — closed only, PnL per lot
══════════════════════════════════════════════ */
function OrdersTable({ orders, loading, pagination, onPage }) {
  const COLS = ['Date', 'Instrument', 'Expiry', 'Signal', 'Strikes', 'Lots', 'PnL / Lot', 'Total PnL'];

  const thStyle = {
    padding: '11px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600,
    color: C.muted2, letterSpacing: '1px', textTransform: 'uppercase',
    borderBottom: `1px solid ${C.border}`, fontFamily: mono, whiteSpace: 'nowrap',
  };
  const tdStyle = { padding: '13px 16px', fontSize: 12.5, fontFamily: mono, verticalAlign: 'middle', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{COLS.map(c => <th key={c} style={thStyle}>{c}</th>)}</tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {COLS.map((_, j) => (
                    <td key={j} style={tdStyle}>
                      <div style={{
                        height: 13, borderRadius: 4, width: `${50 + (j * 9) % 35}%`,
                        background: 'linear-gradient(90deg, #0c1120 25%, #161e32 50%, #0c1120 75%)',
                        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
                      }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : orders.length === 0 ? (
              <tr><td colSpan={COLS.length} style={{ ...tdStyle, textAlign: 'center', color: C.muted2, padding: '48px 20px' }}>No closed trades found</td></tr>
            ) : (
              orders.map(o => {
                const sellStrike = o.sell_leg?.strike;
                const buyStrike  = o.buy_leg?.strike;
                const strikes    = (sellStrike && buyStrike) ? `${sellStrike} / ${buyStrike}` : (sellStrike ?? buyStrike ?? '—');
                const lots       = getLots(o);
                const pnlPerLot  = normalizePnl(o.total_pnl, o);
                const isShort    = (o.signal ?? '').toLowerCase() === 'short';

                return (
                  <tr
                    key={o._id}
                    style={{ borderBottom: `1px solid rgba(30,45,69,0.5)`, transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Date */}
                    <td style={{ ...tdStyle, color: C.muted2, fontSize: 12 }}>{o.created_at?.slice(0, 10) ?? '—'}</td>

                    {/* Instrument */}
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#fff', fontFamily: sans }}>
                      {o.instrument}
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'rgba(61,142,240,0.1)', color: C.blue }}>
                        {o.option_type}
                      </span>
                    </td>

                    {/* Expiry */}
                    <td style={{ ...tdStyle, color: C.muted2, fontSize: 12 }}>{o.expiry}</td>

                    {/* Signal */}
                    <td style={tdStyle}>
                      {pill(
                        (o.signal ?? '—').toUpperCase(),
                        isShort ? C.red : C.green,
                        isShort ? 'rgba(255,77,109,0.1)' : 'rgba(0,229,160,0.1)',
                      )}
                    </td>

                    {/* Strikes */}
                    <td style={{ ...tdStyle, color: C.muted2 }}>{strikes}</td>

                    {/* Lots */}
                    <td style={{ ...tdStyle, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{ fontWeight: 600 }}>{lots}</span>
                      <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>
                        ({o.sell_leg?.quantity ?? o.buy_leg?.quantity ?? '—'} qty)
                      </span>
                    </td>

                    {/* PnL per lot */}
                    <td style={{ ...tdStyle, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: getPnLColor(pnlPerLot) }}>
                      {pnlPerLot != null ? formatPnL(pnlPerLot) : '—'}
                    </td>

                    {/* Total PnL */}
                    <td style={{ ...tdStyle, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: C.muted2 }}>
                      {o.total_pnl != null ? formatPnL(o.total_pnl) : '—'}
                      {lots > 1 && (
                        <span style={{ fontSize: 9, color: C.muted, marginLeft: 4 }}>({lots}L)</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: `1px solid ${C.border}`, fontFamily: mono, fontSize: 11, color: C.muted2 }}>
          <span>Page {pagination.page} of {pagination.total_pages} · {pagination.total} closed trades</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { label: '‹ Prev', disabled: pagination.page <= 1,                       p: pagination.page - 1 },
              { label: 'Next ›', disabled: pagination.page >= pagination.total_pages,  p: pagination.page + 1 },
            ].map(btn => (
              <button
                key={btn.label}
                disabled={btn.disabled}
                onClick={() => onPage(btn.p)}
                style={{ padding: '5px 14px', background: C.surface2, border: `1px solid ${C.border}`, color: btn.disabled ? C.muted : C.muted2, borderRadius: 8, cursor: btn.disabled ? 'not-allowed' : 'pointer', fontFamily: mono, fontSize: 11, opacity: btn.disabled ? 0.4 : 1 }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN STRATEGY PAGE
══════════════════════════════════════════════ */
export default function Strategy() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);

  const [filters, setFilters] = useState({
    signal: '', instrument: '', from_date: '', to_date: '',
    /* status is hardcoded to 'closed' — not exposed in UI */
  });

  const fetchData = useCallback(async (activeFilters = filters, activePage = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(
        Object.entries({
          ...activeFilters,
          status: 'closed',
          page:   activePage,
          limit:  100,
        }).filter(([, v]) => v !== '' && v != null)
      );
      const res     = await strategyAPI.getPerformance(params);
      const payload = res.data?.data ?? res.data;
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Failed to load strategy data');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setPage(1);
    fetchData(next, 1);
  };

  const handleReset = () => {
    const blank = Object.fromEntries(Object.keys(filters).map(k => [k, '']));
    setFilters(blank);
    setPage(1);
    fetchData(blank, 1);
  };


  /* normalize summary stats to 1 lot is not possible server-side
     because different orders have different lot counts.
     So we compute 1-lot equivalents from the orders list client-side
     and use server summary for counts/rates only. */
  const s  = data?.summary      ?? {};
  const dp = data?.daily_pnl    ?? [];
  const bi = data?.by_instrument ?? [];
  const bs = data?.by_signal     ?? [];

  /* normalize daily PnL chart — server sends raw, divide by avg lots
     We can't know exact lots per day from summary, so chart shows
     raw PnL trend as-is with a note. The per-row normalization is
     done in the table where we have full order data. */

  return (
    <div className="st-page" style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 28px 80px', fontFamily: sans }}>
      <style>{`
        @keyframes shimmer { to { background-position: -200% 0; } }
        @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.65)} }
        @media (max-width: 900px) {
          .st-two-col { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .st-page { padding: 16px 16px 60px !important; }
          .st-stat-row > div { flex: 1 1 calc(50% - 7px) !important; min-width: calc(50% - 7px) !important; }
          .st-signal-chart { flex-direction: column !important; align-items: stretch !important; }
          .st-signal-chart > div:first-child { width: 100% !important; }
          .st-filter-bar { flex-direction: column !important; align-items: stretch !important; }
          .st-filter-item { width: 100% !important; }
          .st-filter-item select, .st-filter-item input { width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
          .st-filter-note { display: none !important; }
        }
        @media (max-width: 480px) {
          .st-page { padding: 12px 12px 60px !important; }
          .st-stat-row > div { flex: 1 1 100% !important; min-width: 100% !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.muted, fontFamily: mono, marginBottom: 8 }}>
          <Link to="/dashboard" style={{ color: C.green, textDecoration: 'none' }}>Dashboard</Link>
          <span>›</span>
          <span>Strategy</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', margin: '0 0 6px' }}>
              Spread Strategy
            </h1>
            <p style={{ fontSize: 12, color: C.muted2, fontFamily: mono, margin: 0 }}>
              Live performance · NIFTY &amp; BANKNIFTY · Options spread · Closed trades only
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', borderRadius: 100, fontSize: 11, fontWeight: 700, color: C.green, fontFamily: mono, letterSpacing: '0.8px' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, animation: 'pulse 1.5s ease infinite', flexShrink: 0 }} />
              LIVE TRACK RECORD
            </div>
          </div>
        </div>
      </div>

      {/* ── Disclaimer — 1 line ── */}
      <Disclaimer />

      {/* ── Margin info cards ── */}
      <div className="st-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '16px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: mono, marginBottom: 6 }}>
              Total Margin (w/ Hedge)
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.green }}>₹3,00,000</div>
            <div style={{ fontSize: 10, color: C.muted, fontFamily: mono, marginTop: 4 }}>Capital locked with hedging</div>
          </div>
          <div style={{ fontSize: 28, opacity: 0.2 }}>🛡️</div>
        </div>
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '16px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: mono, marginBottom: 6 }}>
              Margin (w/o Hedge)
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.amber }}>₹5,00,000</div>
            <div style={{ fontSize: 10, color: C.muted, fontFamily: mono, marginTop: 4 }}>Risk if selling naked</div>
          </div>
          <div style={{ fontSize: 28, opacity: 0.2 }}>⚡</div>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <FilterBar filters={filters} onChange={handleFilterChange} onReset={handleReset} />

      {/* ── Error ── */}
      {error && (
        <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.25)', color: C.red, borderRadius: 8, padding: '10px 16px', fontSize: 13, marginBottom: 16, fontFamily: mono }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Stat cards ── */}
      {(() => {
        const avgWin  = s.winning_trades > 0
          ? (data?.orders ?? []).filter(o => (o.total_pnl ?? 0) > 0).reduce((sum, o) => sum + (o.total_pnl ?? 0), 0) / s.winning_trades
          : null;
        const avgLoss = s.losing_trades > 0
          ? (data?.orders ?? []).filter(o => (o.total_pnl ?? 0) < 0).reduce((sum, o) => sum + (o.total_pnl ?? 0), 0) / s.losing_trades
          : null;
        return (
          <div className="st-stat-row" style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard
              label="Total Closed Trades"
              value={loading ? '—' : (s.closed_trades ?? 0)}
              sub={`${s.winning_trades ?? 0} won · ${s.losing_trades ?? 0} lost`}
              accent={C.blue} valColor="#fff"
            />
            <StatCard
              label="Win Rate"
              value={loading ? '—' : `${(s.win_rate ?? 0).toFixed(1)}%`}
              sub="Closed trades only"
              accent={C.amber} valColor={(s.win_rate ?? 0) >= 50 ? C.green : C.red}
            />
            <StatCard
              label="Total PnL"
              value={loading ? '—' : formatPnL(s.total_pnl ?? 0)}
              sub={`Avg/trade: ${loading ? '—' : formatPnL(s.avg_pnl ?? 0)}`}
              accent={C.green} valColor={(s.total_pnl ?? 0) >= 0 ? C.green : C.red}
            />
            <StatCard
              label="Avg Winning Trade"
              value={loading ? '—' : (avgWin != null ? formatPnL(avgWin) : '—')}
              sub={`${s.winning_trades ?? 0} winning trades`}
              accent={C.green} valColor={C.green}
            />
            <StatCard
              label="Avg Losing Trade"
              value={loading ? '—' : (avgLoss != null ? formatPnL(avgLoss) : '—')}
              sub={`${s.losing_trades ?? 0} losing trades`}
              accent={C.red} valColor={C.red}
            />
          </div>
        );
      })()}

      {/* ── Best / Worst ── */}
      <div className="st-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <div style={{ background: 'rgba(0,229,160,0.04)', border: '1px solid rgba(0,229,160,0.15)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: mono, marginBottom: 5 }}>Best Trade (raw)</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.green }}>{loading ? '—' : formatPnL(s.best_trade ?? 0)}</div>
          </div>
          <span style={{ fontSize: 28, opacity: 0.3 }}>🏆</span>
        </div>
        <div style={{ background: 'rgba(255,77,109,0.04)', border: '1px solid rgba(255,77,109,0.15)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: mono, marginBottom: 5 }}>Worst Trade (raw)</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.red }}>{loading ? '—' : formatPnL(s.worst_trade ?? 0)}</div>
          </div>
          <span style={{ fontSize: 28, opacity: 0.3 }}>📉</span>
        </div>
      </div>

      {/* ── Daily PnL chart ── */}
      {dp.length > 0 && (
        <Block title="Cumulative PnL Trend" badge={
          <span style={{ fontSize: 11, color: C.muted2, fontFamily: mono }}>{dp.length} trading days · raw PnL</span>
        }>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dp} margin={{ top: 5, right: 16, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={{ stroke: C.border }} />
              <YAxis tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<DarkTooltip />} />
              <Legend wrapperStyle={{ fontFamily: mono, fontSize: 11, color: C.muted2 }} />
              <Line type="monotone" dataKey="cumulative" name="Cumulative PnL" stroke={C.green} strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: C.green }} />
              <Line type="monotone" dataKey="pnl" name="Daily PnL" stroke={C.blue} strokeWidth={1.5} dot={false} strokeDasharray="4 2" activeDot={{ r: 4, fill: C.blue }} />
            </LineChart>
          </ResponsiveContainer>
        </Block>
      )}

      {/* ── By Instrument + By Signal ── */}
      <div className="st-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 0 }}>

        {bi.length > 0 && (
          <Block title="PnL by Instrument" badge={
            <span style={{ fontSize: 10, color: C.muted2, fontFamily: mono }}>raw PnL</span>
          }>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={bi} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="instrument" tick={{ fill: C.muted2, fontSize: 11, fontFamily: mono }} tickLine={false} axisLine={{ stroke: C.border }} />
                <YAxis tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<DarkTooltip />} />
                <Bar dataKey="total_pnl" name="PnL" radius={[4, 4, 0, 0]}>
                  {bi.map((e, i) => <Cell key={i} fill={e.total_pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {bi.map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, color: '#fff', fontSize: 13, fontFamily: sans }}>{row.instrument}</span>
                    <span style={{ fontSize: 10, color: C.muted2, fontFamily: mono }}>{row.total_trades} trades</span>
                    <span style={{ fontSize: 9, color: C.muted, fontFamily: mono }}>
                      1L = {LOT_SIZE[row.instrument] ?? '?'} qty
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, color: (row.win_rate ?? 0) >= 50 ? C.green : C.red, fontFamily: mono }}>{(row.win_rate ?? 0).toFixed(1)}% WR</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: row.total_pnl >= 0 ? C.green : C.red, fontFamily: mono, fontVariantNumeric: 'tabular-nums' }}>{formatPnL(row.total_pnl)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Block>
        )}

        {bs.length > 0 && (
          <Block title="PnL by Signal">
            <div className="st-signal-chart" style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 }}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={bs} dataKey="total_trades" nameKey="signal" cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={3}>
                    {bs.map((e, i) => <Cell key={i} fill={e.signal === 'SHORT' ? C.red : C.green} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [v, `${n} Trades`]}
                    contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, fontFamily: mono, fontSize: 12 }}
                    itemStyle={{ color: '#e8edf5' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {bs.map((d, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.signal === 'SHORT' ? C.red : C.green, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: mono }}>{d.signal}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: d.total_pnl >= 0 ? C.green : C.red, fontFamily: mono }}>{formatPnL(d.total_pnl)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.muted2, fontFamily: mono, paddingLeft: 18 }}>
                      <span>{d.total_trades} trades</span>
                      <span>{(d.win_rate ?? 0).toFixed(1)}% win rate</span>
                      <span>avg {formatPnL(d.avg_pnl)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={bs} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="signal" tick={{ fill: C.muted2, fontSize: 11, fontFamily: mono }} tickLine={false} axisLine={{ stroke: C.border }} />
                <YAxis tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<DarkTooltip />} />
                <Bar dataKey="total_pnl" name="PnL" radius={[4, 4, 0, 0]}>
                  {bs.map((e, i) => <Cell key={i} fill={e.total_pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Block>
        )}
      </div>

      {/* ── Trade history table ── */}
      <Block title="Closed Trade History" badge={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 10, fontFamily: mono, color: C.muted2 }}>
            {(data?.orders ?? []).length} trades · PnL normalized to 1 lot
          </span>
        </div>
      }>
        <OrdersTable
          orders={data?.orders ?? []}
          loading={loading}
          pagination={null}
          onPage={() => {}}
        />
      </Block>

    </div>
  );
}